/**
 * DesktopLayout — the full desktop rendering shell for PrimateVisionV2.
 * Uses V2 context providers (stream, inference, videoLibrary) for shared state.
 * Accepts explicit props for page-level UI state not in contexts.
 * Extracted from PrimateVisionV2.tsx (PRI-291 Phase 5)
 */
import type { Dispatch, RefObject, SetStateAction } from 'react'
import TermsModal from '../TermsModal'
import ConversionDialog from '../ConversionDialog'
import UpgradeModal from '../UpgradeModal'
import { IconStrip, Sidebar, type OpenPanel, type SidebarVideo } from '../V2Nav'
import { useIsMobile, detectIPad } from '../../core/hooks/useIsMobile'
import { usePrimateAuth } from '../../primate/auth/usePrimateAuth'
import { useStreamContext } from '../../contexts/StreamContext'
import { useInferenceContext } from '../../contexts/InferenceContext'
import { useVideoLibraryContext } from '../../contexts/VideoLibraryContext'
import SourcePreviewCard from '../PrimateVision/SourcePreviewCard'
import { FlyingWebcamClone } from '../../core/components/FlyingWebcamClone'
import ErrorBoundary from '../../core/components/ErrorBoundary'
import { L, type ResultPill, type VideoSource, type QuotaBarData, type CostPreviewData } from '../../core/components/v2/pv2-shared'
import type { CompiledQuery, ParseState } from '../../core/components/v2/compiled-query-types'
import { ProgressBlock, StreamProgressTray, StreamErrorTray } from '../../core/components/v2/V2StreamOverlay'
import { InputBar, VideoSourcePopover } from '../../core/components/v2/V2InputBar'
import { ResultPillComp } from '../../core/components/v2/V2ResultPill'
import { EmptyStateDemo } from '../../core/components/v2/EmptyStateDemo'
import { useMemo } from 'react'
import { trackVideoRenamed } from '../../core/telemetry/events'
import type { UserVideo } from '../../core/lib/api'
import type { LiveInferenceResult } from '../../core/hooks/types'

export interface DesktopLayoutProps {
  // Modal / overlay state
  showTermsForAction: boolean
  handleTermsAccepted: () => void
  showConversionDialog: boolean
  conversionFile: File | null
  handleConversionConfirm: (convertedFile: File) => void | Promise<void>
  handleConversionCancel: () => void
  showUpgradeForDownload: boolean
  setShowUpgradeForDownload: (v: boolean) => void
  // Sidebar
  sidebarOpen: boolean
  setSidebarOpen: (v: boolean) => void
  sidebarPanel: OpenPanel
  setSidebarPanel: (v: OpenPanel) => void
  // Layout state
  beforeStateActive: boolean
  afterState: boolean
  setAfterState: Dispatch<SetStateAction<boolean>>
  carouselVisible: boolean
  carouselDismissed: boolean
  setCarouselDismissed: (v: boolean) => void
  offlineJobActive: boolean
  slotRevealed: boolean
  anyPillApiOpen: boolean
  setAnyPillApiOpen: (v: boolean) => void
  // Input / prompt
  prompt: string
  setPrompt: (v: string) => void
  compiledQuery?: CompiledQuery | null
  parseState?: ParseState
  queryError?: string | null
  onCompiledQueryChange?: (q: CompiledQuery) => void
  onEditModeToggle?: (open: boolean) => void
  videoMenuOpen: boolean
  setVideoMenuOpen: Dispatch<SetStateAction<boolean>>
  // Display state
  showProgress: boolean
  showStreamProgress: boolean
  showCarousel: boolean
  // True once Clerk auth + initial fetches have resolved. Used to defer
  // first paint of the workspace-input-row so a hard refresh doesn't
  // flash the bare "webcam preview + empty input bar" frame before the
  // EmptyStateDemo mounts (Matt 2026-05-26 18:44 #engineering-jeff).
  authLoaded: boolean
  streamProgressDismissing: boolean
  offlineProgressDismissing: boolean
  processingText: string
  processingStep: number
  jobProgress: { stage: string; pct: number; message: string } | null
  completingSteps: boolean
  recordingLimitBannerShown: boolean
  setRecordingLimitBannerShown: Dispatch<SetStateAction<boolean>>
  isStreamingActive: boolean
  streamErrorState: 'idle' | 'error' | 'retrying'
  setStreamErrorState: (v: 'idle' | 'error' | 'retrying') => void
  quotaBar: QuotaBarData | null
  /** PRI-488 F1: pre-Run cost preview (validate_only). */
  costPreview?: CostPreviewData | null
  /** PRI-488 F7: public video id (video_…) for the batch composer. */
  publicVideoId?: string | null
  videoSource: VideoSource | null
  canDownload: boolean
  downloadingPillIds: Set<string>
  // Refs
  streamPillIdRef: RefObject<string | null>
  uploadingPillIdsRef: RefObject<Set<string>>
  autoRetryCountRef: RefObject<number>
  streamWasLiveRef: RefObject<boolean>
  flyDivRef: RefObject<HTMLDivElement | null>
  flyThumbnailRef: RefObject<string | null>
  camRef: RefObject<HTMLDivElement | null>
  desktopThreadAreaRef: RefObject<HTMLDivElement | null>
  threadBottomRef: RefObject<HTMLDivElement | null>
  promptTextareaRef: RefObject<HTMLTextAreaElement | null>
  fileInputRef: RefObject<HTMLInputElement | null>
  videoSlotRefCallback: (el: HTMLDivElement | null) => void
  // Auth display (computed in PrimateVisionV2Inner, not in PrimateAuthContext)
  planTier: string
  userName: string | null
  userInitials: string
  // Ref for stream result tracking
  lastStreamResultRef: RefObject<LiveInferenceResult | null>
  // Handlers
  handleSubmit: () => void
  handleFileSelected: (f: File) => void
  handleDeleteResult: (pill: ResultPill) => void
  handleDownloadPill: (pill: ResultPill) => void
  handleRerunStream: (prompt: string) => void
  attachRenderedVideoForced: (pillId: string) => void
  attachStreamRecordingUrlForced: (pillId: string, recordingUrl: string) => void
  handleSelectUserVideo: (videoId: string) => void
  handleDeleteUserVideo: (videoId: string) => void
  handleSelectDemoVideo: (videoId: string) => void
  handleSelectLiveStreams: () => void
  handleDeleteLiveStreamSessions: () => void
  handleLiveStreamsNewSession: () => void
  handleDeleteDemoVideoSession: (videoId: string) => void
  handleSignOut: () => void
  setSavedUserVideos: Dispatch<SetStateAction<UserVideo[]>>
}

export function DesktopLayout({
  showTermsForAction, handleTermsAccepted,
  showConversionDialog, conversionFile, handleConversionConfirm, handleConversionCancel,
  showUpgradeForDownload, setShowUpgradeForDownload,
  sidebarOpen, setSidebarOpen, sidebarPanel, setSidebarPanel,
  beforeStateActive, afterState,
  carouselVisible: _carouselVisible,
  carouselDismissed,
  setCarouselDismissed,
  offlineJobActive, slotRevealed, anyPillApiOpen, setAnyPillApiOpen,
  prompt, setPrompt, compiledQuery, parseState, queryError, onCompiledQueryChange, onEditModeToggle,
  videoMenuOpen, setVideoMenuOpen,
  showProgress, showStreamProgress, showCarousel, authLoaded, streamProgressDismissing, offlineProgressDismissing,
  processingText, processingStep, jobProgress, completingSteps,
  recordingLimitBannerShown, setRecordingLimitBannerShown, isStreamingActive,
  streamErrorState, setStreamErrorState, quotaBar, costPreview, publicVideoId, videoSource, canDownload, downloadingPillIds,
  streamPillIdRef, uploadingPillIdsRef, autoRetryCountRef, streamWasLiveRef,
  flyDivRef, flyThumbnailRef, camRef, desktopThreadAreaRef, threadBottomRef,
  promptTextareaRef, fileInputRef, videoSlotRefCallback,
  planTier, userName, userInitials, lastStreamResultRef,
  handleSubmit, handleFileSelected, handleDeleteResult, handleDownloadPill, handleRerunStream,
  attachRenderedVideoForced, attachStreamRecordingUrlForced,
  handleSelectUserVideo, handleDeleteUserVideo, handleSelectDemoVideo,
  handleSelectLiveStreams, handleDeleteLiveStreamSessions, handleLiveStreamsNewSession,
  handleDeleteDemoVideoSession, handleSignOut, setSavedUserVideos,
}: DesktopLayoutProps) {
  const streamCtx = useStreamContext()
  const streamSession = streamCtx  // StreamContextValue extends StreamSessionState
  const { isStreaming, setIsStreaming, resultPills, setResultPills, samEnabled, processOffline, setProcessOffline, sourceMode, setSourceMode, frozenFrameUrl, activePrompt, promptSent } = streamCtx
  const { pageState } = useInferenceContext()
  const { demoVideos, regularUserVideos, streamUserVideos, clearSelection } = useVideoLibraryContext()
  const { isSignedIn, isAdmin, isPro, getToken } = usePrimateAuth()
  const isMobile = useIsMobile()
  const iconStripWidth = useMemo(() => detectIPad() ? 56 : 48, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
  <>
    {showTermsForAction && <TermsModal onAccept={handleTermsAccepted} />}
    {showConversionDialog && conversionFile && (
      <ConversionDialog
        file={conversionFile}
        onConfirm={handleConversionConfirm}
        onCancel={handleConversionCancel}
      />
    )}
    <UpgradeModal isOpen={showUpgradeForDownload} onClose={() => setShowUpgradeForDownload(false)} reason="Download session results" />
    <style>{`
      @keyframes pv2-fade-up { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
      @keyframes pv2-pill-enter {
        from { opacity:0; transform:translateY(-16px); }
        to   { opacity:1; transform:translateY(0); }
      }
      @keyframes pv2-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      @keyframes pv2-highlight-pulse {
        0%, 100% { opacity: 0.7; }
        50%      { opacity: 1; }
      }
      @keyframes pv2-highlight-fade-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      * { box-sizing: border-box; }
      textarea { box-sizing: border-box; }
    `}</style>

    <input ref={fileInputRef} type="file" accept="video/*,.mp4,.mov,.avi,.webm,.mkv" style={{ display: 'none' }}
      onChange={e => {
        const f = e.target.files?.[0];
        if (!f) return;
        setResultPills([]);
        void handleFileSelected(f);
        e.target.value = '';
      }}
    />

    {/* Icon strip - shared V2Nav component (single source of truth) */}
    {!isMobile && (
      <IconStrip
        onOpen={(panel) => { setSidebarPanel(panel); setSidebarOpen(true); }}
        isSignedIn={isSignedIn}
        isAdmin={isAdmin}
        userInitials={userInitials}
      />
    )}

    {/* Sidebar - shared V2Nav component (single source of truth) */}
    <ErrorBoundary fallback={<div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: 280, background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: '#888', fontSize: 12 }}>Sidebar unavailable — refresh to continue</span></div>}>
    <Sidebar
      open={sidebarOpen}
      onClose={() => setSidebarOpen(false)}
      isSignedIn={isSignedIn}
      isAdmin={isAdmin}
      initialPanel={sidebarPanel}
      userInitials={userInitials}
      userName={userName}
      isPro={isPro}
      planTier={planTier}
      demoVideos={demoVideos as SidebarVideo[]}
      userVideos={regularUserVideos as SidebarVideo[]}
      streamSessionCount={streamUserVideos.length}
      onLiveStreamsSelect={handleSelectLiveStreams}
      onLiveStreamsDelete={handleDeleteLiveStreamSessions}
      onLiveStreamsNewSession={handleLiveStreamsNewSession}
      onSignOut={handleSignOut}
      onUserVideoSelect={handleSelectUserVideo}
      onDemoVideoSelect={handleSelectDemoVideo}
      onUserVideoRename={(id, name) => {
        setSavedUserVideos(prev => prev.map(v => v.id === id ? { ...v, display_name: name } : v));
        trackVideoRenamed({ video_id: id, user_plan: 'unknown' });
      }}
      onUserVideoDelete={handleDeleteUserVideo}
      onDemoVideoDeleteSession={handleDeleteDemoVideoSession}
    />
    </ErrorBoundary>

    {/* Full-screen canvas.
         overflow:clip (not hidden) — Safari's compositor can break hit-testing inside
         overflow:hidden containers that also have CSS transitions. */}
    <div style={{
      position: 'fixed', inset: 0,
      marginLeft: isMobile ? 0 : sidebarOpen ? 0 : iconStripWidth, // respect icon strip width
      background: L.bgPrimary,
      display: 'flex', flexDirection: 'column',
      overflow: 'clip',
      transition: 'margin-left 220ms cubic-bezier(0.4,0,0.2,1)',
    }}>
      {/* ── Unified desktop layout: stays mounted during BEFORE state AND streaming.
           This prevents the video black-flash caused by unmounting/remounting <video>
           elements when switching between BEFORE and AFTER layout trees.
           The layout transitions between states via CSS (no conditional mount/unmount). ── */}
      {/* PRI-519: thread-area gets minHeight:0 + overflowY:auto in AFTER state. Without
           them a flex child's implicit min-height:auto lets thread-area grow to content
           height inside the fixed overflow:clip canvas — once result pills exceed the
           viewport, the InputBar below them is clipped and unreachable (no scroll
           container exists; mouse wheel does nothing). BEFORE state keeps
           overflow:visible so popovers/pickers can extend past the centred row. */}
      {!isMobile && (beforeStateActive || isStreaming || offlineJobActive || (afterState && resultPills.length > 0)) && (
        <div ref={desktopThreadAreaRef} data-testid="thread-area" data-after-state={afterState ? 'true' : 'false'} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '0 24px 20px', overflowY: afterState ? 'auto' : 'visible', overflowX: afterState ? 'clip' : 'visible', position: 'relative', zIndex: 2 }}>
          {/* Top centering spacer: flex-grow 1 (centered in BEFORE) → 0 (top-aligned in AFTER).
               Matches mockup's padding-top transition but works with unknown container height. */}
          <div style={{ flexGrow: afterState ? 0 : 1, flexShrink: 0, flexBasis: '0px', transition: 'flex-grow 0.52s cubic-bezier(0.4,0,0.2,1)' }} />
          <ErrorBoundary fallback={<div style={{ width: '100%', padding: 24, textAlign: 'center', color: '#888', fontSize: 14 }}>Result area unavailable — refresh to continue</div>}>
          <div style={{ width: '100%', maxWidth: anyPillApiOpen ? 1400 : 900, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, transition: 'max-width 200ms ease', ...(afterState ? { flex: 1 } : {}) }}>
            {/* Result pill(s) — wrapper animates max-height on mount so the pill
                 grows from 0, preventing a one-frame layout jump that shifts the slot. */}
            {resultPills.map(pill => (
              <div key={pill.id} style={{
                width: '100%', display: 'flex', justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
                position: 'relative', zIndex: 5,
                animation: 'pv2-pill-enter 0.5s cubic-bezier(0.4,0,0.2,1) both',
              }}>
                <ResultPillComp
                  pill={pill}
                  connectionState={streamSession.connectionState}
                  errorMessage={pill.id === streamPillIdRef.current ? streamSession.errorMessage : null}
                  remoteStream={pill.id === streamPillIdRef.current ? streamSession.remoteStream : null}
                  localStream={pill.id === streamPillIdRef.current ? streamSession.localStream : null}
                  slotRevealed={slotRevealed}
                  noFadeIn={!slotRevealed}
                  videoSlotRef={pill.id === streamPillIdRef.current ? videoSlotRefCallback : undefined}
                  onStop={pill.id === streamPillIdRef.current ? () => { streamSession.stopStream(); setIsStreaming(false); } : undefined}
                  onPlayVideo={
                    (!pill.id.startsWith('stream-') && !pill.videoUrl)
                      ? () => attachRenderedVideoForced(pill.id)
                    : (pill.id.startsWith('stream-') && !pill.videoUrl && !!pill.recordingUrl && pill.id !== streamPillIdRef.current)
                      ? () => attachStreamRecordingUrlForced(pill.id, pill.recordingUrl!)
                    : undefined
                  }
                  onRerunStream={pill.id.startsWith('stream-') && pill.id !== streamPillIdRef.current && !pill.videoUrl && !pill.recordingUrl && !uploadingPillIdsRef.current.has(pill.id) ? handleRerunStream : undefined}
                  isSaving={uploadingPillIdsRef.current.has(pill.id)}
                  onDeleteResult={() => handleDeleteResult(pill)}
                  onDownload={() => handleDownloadPill(pill)}
                  isDownloading={downloadingPillIds.has(pill.id)}
                  canDownload={canDownload}
                  isStreamActive={(pill.id === streamPillIdRef.current && isStreaming) || (pill.id.startsWith('offline-') && offlineJobActive)}
                  isFileStream={pill.id === streamPillIdRef.current && streamSession.isFileStream}
                  isFrontCamera={streamSession.isFrontCamera}
                  onApiOpenChange={setAnyPillApiOpen}
                  planTier={planTier}
                />
              </div>
            ))}

            {/* Spacer: pushes carousel + InputBar to bottom when afterState.
                 In BEFORE state this is 0 (content stays centred via parent spacers).
                 In AFTER state this grows to fill remaining space. */}
            <div style={{
              flexGrow: afterState ? 1 : 0,
              flexShrink: 1,
              flexBasis: '0px',
              transition: 'flex-grow 0.52s cubic-bezier(0.4,0,0.2,1)',
            }} />

            {/* Onboarding v3 (2026-05-23, dev only): EmptyStateDemo replaces the
                reverted DemoReelPlayer. Mounts the real InputBar + ResultPillComp
                driven by a fixture so the surface is indistinguishable from a real
                session. Unmounts as soon as the user picks a file or starts a stream
                (or when carouselDismissed flips true via the CTA, 2026-05-25). */}
            {showCarousel && !carouselDismissed && (
              <div
                data-testid="empty-state-demo-slot"
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: 14,
                  animation: 'pv2-pill-enter 0.5s cubic-bezier(0.4,0,0.2,1) both',
                }}
              >
                <EmptyStateDemo
                  onTryForYourself={() => {
                    // Dismiss the carousel so the workspace input row reveals,
                    // then open the VideoSourcePopover so the user can pick
                    // Webcam vs File. This is the only path that ever asks
                    // for camera permission (per PRI-onboarding-v3).
                    setCarouselDismissed(true)
                    setVideoMenuOpen(true)
                  }}
                  onUserTakeover={() => setCarouselDismissed(true)}
                  isSignedIn={!!isSignedIn}
                  onPersistentDismiss={() => setCarouselDismissed(true)}
                />
              </div>
            )}

            {/* Row: SourcePreviewCard + InputBar wrapper.
                 Onboarding v3 (2026-05-25): while the EmptyStateDemo video is autoplaying
                 (showCarousel && !carouselDismissed), hide the real input row entirely so
                 the video + "Try for yourself" CTA are the hero of the screen. After the
                 user clicks the CTA, the demo unmounts, carouselDismissed flips to true,
                 and this row reveals — taking the VideoSourcePopover with it. */}
            <div
              data-testid="workspace-input-row"
              style={{
                // Hide the workspace input row while:
                //   (a) auth is still hydrating — prevents the bare empty
                //       input bar from flashing on hard refresh before we
                //       know whether to mount the demo, AND
                //   (b) the EmptyStateDemo is in control of the surface.
                display:
                  !authLoaded || (showCarousel && !carouselDismissed)
                    ? 'none'
                    : 'flex',
                alignItems: 'stretch', gap: 14, width: '100%',
              }}
            >
              <div
                data-source-wrapper
                style={{
                  ...(afterState ? { opacity: 0, width: 0, minWidth: 0, overflow: 'hidden' as const, pointerEvents: 'none' as const, padding: 0 } : {}),
                  /* Mockup timing: opacity fades first (0.28s), width collapses after
                     a 0.18s delay (0.42s). This prevents the row reflow from shifting
                     the pill during the critical first 180ms of the fly. */
                  transition: 'opacity 0.28s ease 0s, width 0.42s cubic-bezier(0.4,0,0.2,1) 0.18s, min-width 0.42s ease 0.18s, padding 0.42s ease 0.18s',
                }}
              >
                <SourcePreviewCard
                  ref={camRef}
                  sourceMode={sourceMode}
                  videoSource={videoSource}
                  localStream={streamSession.localStream}
                  frozenFrameUrl={frozenFrameUrl}
                  isFileStream={streamSession.isFileStream}
                />
              </div>
              {/* Height-animated input pill wrapper - 201px in BEFORE, 133px in AFTER.
                   No overflow clipping — the Advanced tray needs to expand beyond the
                   fixed height. The main canvas container (position:fixed parent) already
                   has overflow:clip for the Safari hit-test fix. */}
              <div style={{
                flex: 1, position: 'relative', display: 'flex', flexDirection: 'column',
                minHeight: afterState ? 133 : 201,
                transition: 'min-height 0.5s cubic-bezier(0.4,0,0.2,1)',
              }}>
                {videoMenuOpen && (
                  <VideoSourcePopover
                    onClose={() => setVideoMenuOpen(false)}
                    onUpload={() => fileInputRef.current?.click()}
                    onStream={(deviceId?: string) => {
                      setSourceMode('webcam'); // first-use UX: mark source as explicitly chosen
                      if (deviceId) streamSession.selectDevice(deviceId);
                      setIsStreaming(true);
                      lastStreamResultRef.current = null;
                      setVideoMenuOpen(false);
                      void streamSession.requestCameraAccess();
                      if (getToken) void getToken().then(t => streamSession.prewarmTurn(t)).catch(() => {});
                    }}
                    devices={streamSession.devices}
                    selectedDeviceId={streamSession.selectedDeviceId}
                    onSelectDevice={streamSession.selectDevice}
                  />
                )}
                {showProgress && (
                  <ProgressBlock step={processingStep} processingText={processingText} samEnabled={samEnabled} jobProgress={jobProgress} completingSteps={completingSteps} dismissing={offlineProgressDismissing} />
                )}
                <InputBar
                  videoSource={videoSource}
                  onSwitchToWebcam={() => {
                    clearSelection();
                    streamSession.reacquireCamera();
                  }}
                  onSwitchToFile={() => { streamSession.cancelPreConnect(); streamSession.releaseCamera(); }}
                  onClearVideo={() => {
                    if (isStreaming) { setIsStreaming(false); streamSession.stopStream(); }
                    streamSession.releaseCamera();
                    clearSelection();
                    setResultPills([]);
                  }}
                  prompt={prompt}
                  onPromptChange={setPrompt}
                  onSubmit={handleSubmit}
                  onOpenVideoMenu={() => setVideoMenuOpen(v => !v)}
                  onRequestFile={() => fileInputRef.current?.click()}
                  processOffline={processOffline}
                  onProcessOfflineChange={setProcessOffline}
                  disabled={pageState === 'processing' || pageState === 'uploading'}
                  localStream={streamSession.localStream}
                  frozenFrameUrl={frozenFrameUrl}
                  quotaBar={quotaBar}
                  costPreview={costPreview}
                  publicVideoId={publicVideoId}
                  streamingMetering={isStreaming ? streamSession.metering : null}
                  isStreaming={isStreaming}
                  promptSent={promptSent}
                  hasResultPills={false}
                  isFileStream={streamSession.isFileStream}
                  fileDuration={streamSession.fileDuration}
                  fileCurrentTime={streamSession.fileCurrentTime}
                  cameraDevices={streamSession.devices}
                  selectedCameraId={streamSession.selectedDeviceId}
                  onSelectCamera={streamSession.selectDevice}
                  sourceModeOverride={sourceMode}
                  onSourceModeChange={setSourceMode}
                  textareaRef={promptTextareaRef}
                  hideVideoChip={isStreaming || sourceMode === 'file'}
                  isFrontCamera={streamSession.isFrontCamera}
                  compiledQuery={compiledQuery}
                  parseState={parseState}
                  parseError={queryError}
                  onCompiledQueryChange={onCompiledQueryChange}
                  onEditModeToggle={onEditModeToggle}
                  planTier={planTier}
                  fillHeight
                />
              </div>
            </div>

            {/* Phase 1 (2026-05-23): "Primate Vision may make mistakes" disclaimer removed
                per product directive — Darwin is positioned as accurate, not hedged. */}
          </div>
          </ErrorBoundary>
          {/* Bottom centering spacer — mirrors top spacer */}
          <div style={{ flexGrow: afterState ? 0 : 1, flexShrink: 0, flexBasis: '0px', transition: 'flex-grow 0.52s cubic-bezier(0.4,0,0.2,1)' }} />
        </div>
      )}

      {/* ── AFTER state (non-streaming desktop or mobile): scrollable thread + fixed bottom bar.
           Only renders when NOT streaming — streaming uses the unified layout above. ── */}
      {((!beforeStateActive && !isStreaming && !offlineJobActive && !(afterState && resultPills.length > 0)) || isMobile) && <>
      {/* Scrollable thread */}
      <div data-testid="thread-area" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{
          width: '100%', maxWidth: anyPillApiOpen ? 1400 : 960,
          padding: '48px 24px 24px',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 20, minHeight: '100%',
          transition: 'max-width 200ms ease',
          justifyContent: resultPills.length === 0 ? 'center' : 'flex-start',
        }}>
          {/* Phase 1 (2026-05-23): ProductCarousel removed from workspace empty state.
              The empty state is intentionally minimal until Phase 2 (Demo Reel) replaces it. */}
          {/* Progress and stream trays are now above the input bar */}
          {resultPills.map(pill => (
            <div key={pill.id} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <ResultPillComp
                pill={pill}
                connectionState={streamSession.connectionState}
                errorMessage={pill.id === streamPillIdRef.current ? streamSession.errorMessage : null}
                remoteStream={pill.id === streamPillIdRef.current ? streamSession.remoteStream : null}
                localStream={pill.id === streamPillIdRef.current ? streamSession.localStream : null}
                videoSlotRef={undefined /* fly already triggered from BEFORE layout */}
                planTier={planTier}
                onPlayVideo={
                  // Uploaded/demo pill with no videoUrl yet: fetch from inference server
                  (!pill.id.startsWith('stream-') && !pill.videoUrl)
                    ? () => attachRenderedVideoForced(pill.id)
                  // Stream pill with a recording URL but videoUrl not yet resolved:
                  // force-attach the CloudFront URL (HEAD check may have failed/timed out)
                  : (pill.id.startsWith('stream-') && !pill.videoUrl && !!pill.recordingUrl && pill.id !== streamPillIdRef.current)
                    ? () => attachStreamRecordingUrlForced(pill.id, pill.recordingUrl!)
                  : undefined
                }
                // Re-run overlay only for stream pills with NO recording at all (legacy sessions)
                // and not currently uploading their recording
                onRerunStream={pill.id.startsWith('stream-') && pill.id !== streamPillIdRef.current && !pill.videoUrl && !pill.recordingUrl && !uploadingPillIdsRef.current.has(pill.id) ? handleRerunStream : undefined}
                isSaving={uploadingPillIdsRef.current.has(pill.id)}
                onDeleteResult={() => handleDeleteResult(pill)}
                onApiOpenChange={setAnyPillApiOpen}
                onDownload={() => handleDownloadPill(pill)}
                isDownloading={downloadingPillIds.has(pill.id)}
                canDownload={canDownload}
                isStreamActive={(pill.id === streamPillIdRef.current && isStreaming) || (pill.id.startsWith('offline-') && offlineJobActive)}
                onStop={pill.id.startsWith('stream-') && pill.id === streamPillIdRef.current ? () => {
                  // Capture last frame from remoteStream before stopping
                  const rs = streamSession.remoteStream;
                  if (rs) {
                    const track = rs.getVideoTracks()[0];
                    if (track) {
                      try {
                        const cap = new (window as any).ImageCapture(track);
                        cap.grabFrame().then((bm: ImageBitmap) => {
                          const cv = document.createElement('canvas');
                          cv.width = bm.width; cv.height = bm.height;
                          // Mirror only for front-camera webcam streams
                          const ctx = cv.getContext('2d');
                          if (ctx) {
                            if (!streamSession.isFileStream && streamSession.isFrontCamera) {
                              ctx.translate(bm.width, 0);
                              ctx.scale(-1, 1);
                            }
                            ctx.drawImage(bm, 0, 0);
                          }
                          const dataUrl = cv.toDataURL('image/jpeg', 0.8);
                          setResultPills(prev => prev.map(p =>
                            p.id === streamPillIdRef.current ? { ...p, videoThumb: dataUrl } : p
                          ));
                        }).catch(() => {});
                      } catch {}
                    }
                  }
                  streamSession.stopStream();
                  setIsStreaming(false);
                } : undefined}
                isFileStream={pill.id === streamPillIdRef.current && streamSession.isFileStream}
                isFrontCamera={streamSession.isFrontCamera}
              />
            </div>
          ))}
          <div ref={threadBottomRef} />
        </div>
      </div>

      {/* Phase 1 (2026-05-23): compressed BeforeStateCarousel removed (was above fixed input bar in AFTER state). */}

      {/* Fixed input bar */}
      <div style={{ flexShrink: 0, padding: '0 24px 20px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1020, position: 'relative' }}>
          {videoMenuOpen && (
            <VideoSourcePopover
              onClose={() => setVideoMenuOpen(false)}
              onUpload={() => fileInputRef.current?.click()}
              onStream={(deviceId?: string) => {
                setSourceMode('webcam'); // first-use UX: mark source as explicitly chosen
                if (deviceId) streamSession.selectDevice(deviceId);
                setIsStreaming(true);
                lastStreamResultRef.current = null; // clear stale result from previous session
                setVideoMenuOpen(false);
                void streamSession.requestCameraAccess();
                // Pre-warm TURN credentials while user types their prompt
                if (getToken) void getToken().then(t => streamSession.prewarmTurn(t)).catch(() => {});
              }}
              devices={streamSession.devices}
              selectedDeviceId={streamSession.selectedDeviceId}
              onSelectDevice={streamSession.selectDevice}
            />
          )}
          {recordingLimitBannerShown && isStreamingActive && (
            <div
              onClick={() => setRecordingLimitBannerShown(false)}
              style={{
                position: 'absolute',
                bottom: 'calc(100% + 10px)',
                left: 0,
                right: showStreamProgress ? 310 : 0,
                background: `${L.signal}18`,
                border: `1px solid ${L.signal}40`,
                borderRadius: 10,
                padding: '8px 14px',
                fontSize: 11,
                fontFamily: '"JetBrains Mono", monospace',
                color: L.signal,
                cursor: 'pointer',
                zIndex: 49,
                letterSpacing: '0.02em',
              }}
            >
              Only the first {streamSession.recordingMaxMinutes} min of this session will be saved to your history.
            </div>
          )}
          {showStreamProgress && (
            <StreamProgressTray
              connectionState={streamSession.connectionState}
              prompt={activePrompt}
              queue={streamSession.queue}
              dismissing={streamProgressDismissing}
              connectProgress={streamSession.connectProgress}
              onCancel={() => {
                streamSession.cancelPreConnect()
                setIsStreaming(false)
              }}
            />
          )}
          {(streamErrorState === 'error' || streamErrorState === 'retrying') && !isStreamingActive && (
            <StreamErrorTray
              errorMessage={streamSession.errorMessage ?? null}
              isRetrying={streamErrorState === 'retrying'}
              retryAttempt={autoRetryCountRef.current}
              onRetry={() => {
                autoRetryCountRef.current = 0;
                streamWasLiveRef.current = false;
                setStreamErrorState('idle');
                streamSession.reset();
                setIsStreaming(true);
                void streamSession.requestCameraAccess();
              }}
              onDismiss={() => {
                autoRetryCountRef.current = 0;
                streamWasLiveRef.current = false;
                setStreamErrorState('idle');
                setIsStreaming(false);
                streamSession.reset();
              }}
            />
          )}
          {showProgress && (
            <ProgressBlock step={processingStep} processingText={processingText} samEnabled={samEnabled} jobProgress={jobProgress} completingSteps={completingSteps} dismissing={offlineProgressDismissing} />
          )}
          <InputBar
            videoSource={videoSource}
            onSwitchToWebcam={() => {
              clearSelection();
              streamSession.reacquireCamera();
            }}
            onSwitchToFile={() => { streamSession.cancelPreConnect(); streamSession.releaseCamera(); }}
            onClearVideo={() => {
              if (isStreaming) {
                setIsStreaming(false);
                streamSession.stopStream();
              }
              // Release camera: stops tracks AND clears selectedDeviceId AND sets cameraReleasedRef
              // so the re-acquisition effect doesn't immediately restart the camera.
              streamSession.releaseCamera();
              clearSelection();
              // Clear session history - return to blank slate.
              // The video is NOT deleted; it remains accessible via the sidebar.
              setResultPills([]);
            }}
            prompt={prompt}
            onPromptChange={setPrompt}
            onSubmit={handleSubmit}
            onOpenVideoMenu={() => setVideoMenuOpen(v => !v)}
            onRequestFile={() => fileInputRef.current?.click()}
            processOffline={processOffline}
            onProcessOfflineChange={setProcessOffline}
            disabled={pageState === 'processing' || pageState === 'uploading'}
            localStream={streamSession.localStream}
            frozenFrameUrl={frozenFrameUrl}
            quotaBar={quotaBar}
            costPreview={costPreview}
            publicVideoId={publicVideoId}
            streamingMetering={isStreaming ? streamSession.metering : null}
            isStreaming={isStreaming}
            promptSent={promptSent}
            hasResultPills={resultPills.length > 0}
            isFileStream={streamSession.isFileStream}
            fileDuration={streamSession.fileDuration}
            fileCurrentTime={streamSession.fileCurrentTime}
            cameraDevices={streamSession.devices}
            selectedCameraId={streamSession.selectedDeviceId}
            onSelectCamera={streamSession.selectDevice}
            hideVideoChip={isStreaming || sourceMode === 'file'}
            isFrontCamera={streamSession.isFrontCamera}
            compiledQuery={compiledQuery}
            parseState={parseState}
            parseError={queryError}
            onCompiledQueryChange={onCompiledQueryChange}
            onEditModeToggle={onEditModeToggle}
            planTier={planTier}
          />
          {/* Phase 1 (2026-05-23): disclaimer removed (was previously below input bar in AFTER state). */}
        </div>
      </div>
      </>}

      {/* Flying webcam clone overlay - position/visibility controlled directly
           via flyDivRef (plain DOM, matching Tom's mockup approach). No React
           state is used for the clone's position to avoid render-cycle latency. */}
      <FlyingWebcamClone ref={flyDivRef}>
        {/* For file streams: show the captured thumbnail image (the file
             captureStream isn't ready yet when the fly starts).
             For webcam: show the live camera feed as before. */}
        {flyThumbnailRef.current ? (
          <img
            src={flyThumbnailRef.current}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
          />
        ) : streamSession.localStream ? (
          <video
            ref={el => {
              if (el && streamSession.localStream) {
                el.srcObject = streamSession.localStream;
                el.muted = true;
                el.play().catch(() => {});
              }
            }}
            autoPlay muted playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: (!streamSession.isFileStream && streamSession.isFrontCamera) ? 'scaleX(-1)' : 'none' }}
            {...({ 'webkit-playsinline': 'true' } as React.HTMLAttributes<HTMLVideoElement>)}
          />
        ) : null}
      </FlyingWebcamClone>
    </div>
  </>
  )
}
